import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import { ArrowLeft, HeartCrack, RotateCcw, BookOpen, Sparkles, X, Trash2, Plus } from "lucide-react";

const ENCOURAGEMENTS = [
  "Každý den je vítězství. ❤️",
  "Tvoje hodnota nezávisí na něm.",
  "Vracíš se k sobě, a to je krásné.",
  "Pomalu. Postupně. Pevně.",
  "Vesmír drží svobodu, ne minulost.",
  "Tvé srdce ví, co dělá.",
];

export default function Breakup() {
  const nav = useNavigate();
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journal, setJournal] = useState([]);
  const [showStart, setShowStart] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [nc, j] = await Promise.all([
        api.get("/nocontact").catch(() => ({ data: null })),
        api.get("/journal").catch(() => ({ data: [] })),
      ]);
      setTracker(nc.data);
      setJournal(j.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const enc = ENCOURAGEMENTS[(tracker?.days || 0) % ENCOURAGEMENTS.length];

  return (
    <div className="app-shell bg-aurora min-h-screen pb-nav" data-testid="breakup-page">
      <div className="px-6 pt-10 pb-5 flex items-center gap-3">
        <button onClick={() => nav("/home")} className="text-[#5F7F77]" data-testid="breakup-back"><ArrowLeft size={20} strokeWidth={1.7} /></button>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#5F7F77] font-medium">Sentra Coach</p>
          <h1 className="font-display text-3xl text-[#1A2924] leading-tight">Po rozchodu</h1>
        </div>
      </div>

      <div className="px-6 space-y-5">
        {/* No-contact tracker */}
        {!loading && !tracker && (
          <div className="rounded-3xl p-6 bg-gradient-to-br from-[#082A24] to-[#0F3D34] text-white shadow-lg" data-testid="nc-empty">
            <HeartCrack size={28} className="mb-3" strokeWidth={1.6} />
            <h2 className="font-display text-2xl mb-2">No-contact tracker</h2>
            <p className="text-sm text-white/85 leading-relaxed mb-5">
              Začni si počítat dny bez kontaktu. Tvůj mozek potřebuje 21+ dní, aby přerušil emoční závislost. Já tě v tom budu doprovázet.
            </p>
            <button onClick={() => setShowStart(true)} className="bg-white text-[#0F3D34] rounded-full px-5 py-2.5 text-sm font-medium active:scale-95" data-testid="nc-start-btn">
              Začít no-contact
            </button>
          </div>
        )}

        {tracker && (
          <div className="glass-card p-6" data-testid="nc-tracker">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#5F7F77]">No-contact</p>
                <h2 className="font-display text-xl text-[#1A2924]">
                  {tracker.ex_name ? `Bez ${tracker.ex_name}` : "Tvůj streak"}
                </h2>
              </div>
              <button onClick={async () => { if (window.confirm("Opravdu ukončit tracker?")) { await api.delete("/nocontact"); load(); } }} className="text-[#5F7F77]" data-testid="nc-stop">
                <X size={18} />
              </button>
            </div>
            <div className="text-center py-4">
              <p className="font-display text-7xl text-[#0F3D34] leading-none" data-testid="nc-days">{tracker.days}</p>
              <p className="text-xs uppercase tracking-widest text-[#5F7F77] mt-2">{tracker.days === 1 ? "den" : tracker.days < 5 ? "dny" : "dní"} bez kontaktu</p>
            </div>
            <p className="text-center text-sm italic text-[#C9A66B] mb-4">„{enc}"</p>
            <div className="grid grid-cols-2 gap-2 text-center text-xs text-[#5F7F77]">
              <div className="bg-[#FFE4CC] rounded-2xl py-2.5"><strong className="text-[#1A2924] block text-base">{tracker.best_streak || tracker.days}</strong> nejlepší streak</div>
              <div className="bg-[#FFE4CC] rounded-2xl py-2.5"><strong className="text-[#1A2924] block text-base">{tracker.slips?.length || 0}</strong> {tracker.slips?.length === 1 ? "selhání" : "selhání"}</div>
            </div>
            <button onClick={async () => { if (window.confirm("Resetovat? Selhání není konec — jen restart.")) { await api.post("/nocontact/reset"); load(); } }} className="btn-secondary mt-4 flex items-center justify-center gap-2 text-sm" data-testid="nc-reset">
              <RotateCcw size={16} /> Resetovat (selhala jsem)
            </button>
          </div>
        )}

        {/* Journal */}
        <div className="glass-card p-6" data-testid="journal-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BookOpen size={18} className="text-[#C9A66B]" strokeWidth={1.7} />
              <h3 className="font-display text-xl text-[#1A2924]">Deník uzdravení</h3>
            </div>
            <Link to="/breakup/journal/new" data-testid="journal-add">
              <button className="w-9 h-9 rounded-full bg-[#0F3D34] text-white flex items-center justify-center"><Plus size={16} /></button>
            </Link>
          </div>
          {journal.length === 0 ? (
            <p className="text-sm text-[#5F7F77] text-center py-6">Zatím žádný záznam.<br />Sepiš, jak se cítíš. ❤️</p>
          ) : (
            <div className="space-y-2">
              {journal.slice(0, 3).map((j) => (
                <div key={j.id} className="border border-[#DEE7E2] rounded-2xl p-3" data-testid={`journal-${j.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[#5F7F77]">{new Date(j.created_at).toLocaleDateString("cs-CZ", { day: "numeric", month: "long" })}</span>
                    <span className="text-xs text-[#C9A66B]">nálada {j.mood}/10</span>
                  </div>
                  <p className="text-sm text-[#1A2924] line-clamp-2">{j.content}</p>
                </div>
              ))}
              {journal.length > 3 && (
                <Link to="/breakup/journal" className="block text-center text-xs text-[#C9A66B] underline mt-2" data-testid="journal-see-all">
                  Zobrazit všechny ({journal.length})
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Analyze ex messages */}
        <Link to="/analyze" data-testid="breakup-analyze">
          <div className="rounded-3xl p-5 bg-gradient-to-br from-[#F0E8D6] to-[#EDE5D2] border border-[#DEE7E2] flex items-center gap-3 active:scale-[0.98] transition">
            <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center"><Sparkles size={18} className="text-[#C9A66B]" /></div>
            <div className="flex-1">
              <p className="font-display text-lg text-[#1A2924]">Analyzovat zprávy od ex</p>
              <p className="text-xs text-[#5F7F77]">AI ti pomůže nepodléhat manipulaci</p>
            </div>
          </div>
        </Link>
      </div>

      {showStart && <StartModal onClose={() => setShowStart(false)} onStarted={() => { setShowStart(false); load(); }} />}
      <BottomNav />
    </div>
  );
}

function StartModal({ onClose, onStarted }) {
  const [exName, setExName] = useState("");
  const [reasons, setReasons] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/nocontact/start", { ex_name: exName, reasons });
      onStarted();
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto" data-testid="nc-start-modal">
        <div className="w-12 h-1 bg-[#FFE4CC] rounded-full mx-auto mb-4" />
        <h2 className="font-display text-2xl text-[#1A2924] mb-1">Začít no-contact</h2>
        <p className="text-sm text-[#54635F] mb-5">Pomalu, ale jistě. Pojď si stanovit záměr.</p>
        <div className="space-y-3 mb-5">
          <div>
            <label className="label-soft">Jméno ex (volitelné)</label>
            <input className="input-field" value={exName} onChange={(e) => setExName(e.target.value)} placeholder="Např. Martin" data-testid="nc-ex-name" />
          </div>
          <div>
            <label className="label-soft">Proč to dělám</label>
            <textarea className="input-field text-sm" rows={3} value={reasons} onChange={(e) => setReasons(e.target.value)} placeholder="Sepiš si důvody. V slabé chvíli ti to pomůže." data-testid="nc-reasons" />
          </div>
        </div>
        <button onClick={submit} disabled={loading} className="btn-primary" data-testid="nc-confirm-start">
          {loading ? "Začínám..." : "Začít teď"}
        </button>
        <button onClick={onClose} className="block w-full text-center text-sm text-[#5F7F77] mt-3">Zrušit</button>
      </div>
    </div>
  );
}
